import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getCoachees, getDocuments, getRecentActivity } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  UserPlus, 
  Calendar, 
  Clock, 
  ArrowRight,
  FileText,
  Folder,
  File,
  Image,
  FileSpreadsheet,
  Presentation,
  Video,
  Music,
  Archive,
  ExternalLink,
  Loader2,
  ClipboardList,
  UserCheck,
  Edit3
} from 'lucide-react';

// Helper to get file icon based on mime type
const getFileIcon = (mimeType) => {
  if (!mimeType) return File;
  if (mimeType.includes('folder')) return Folder;
  if (mimeType.includes('document') || mimeType.includes('text') || mimeType.includes('pdf')) return FileText;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return FileSpreadsheet;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return Presentation;
  if (mimeType.includes('image')) return Image;
  if (mimeType.includes('video')) return Video;
  if (mimeType.includes('audio')) return Music;
  if (mimeType.includes('zip') || mimeType.includes('archive')) return Archive;
  return File;
};

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// Activity icon mapper
const getActivityIcon = (type) => {
  switch (type) {
    case 'coachee_created':
      return UserPlus;
    case 'session_created':
      return ClipboardList;
    case 'coachee_updated':
      return Edit3;
    default:
      return Clock;
  }
};

// Activity color mapper
const getActivityColor = (type) => {
  switch (type) {
    case 'coachee_created':
      return 'bg-green-100 text-green-600';
    case 'session_created':
      return 'bg-blue-100 text-blue-600';
    case 'coachee_updated':
      return 'bg-amber-100 text-amber-600';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

export default function CoachDashboard() {
  const { user } = useAuth();
  const [coachees, setCoachees] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [coacheesData, docsData, activityData] = await Promise.all([
        getCoachees(),
        getDocuments(),
        getRecentActivity()
      ]);
      setCoachees(coacheesData);
      setDocuments(docsData);
      setActivities(activityData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
      setLoadingDocs(false);
      setLoadingActivity(false);
    }
  };

  const stats = [
    { label: 'Total Coachees', value: coachees.length, icon: Users, color: 'bg-blue-900' },
    { label: 'Sesiones Registradas', value: activities.filter(a => a.type === 'session_created').length, icon: Calendar, color: 'bg-sky-500' },
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="coach-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-heading text-3xl sm:text-4xl text-slate-800">
              ¡Hola, {user?.nombre?.split(' ')[0]}!
            </h1>
            <p className="text-slate-500 mt-1">
              Aquí tienes un resumen de tu actividad
            </p>
          </div>
          <Link to="/coachees/new">
            <Button className="btn-primary flex items-center gap-2" data-testid="new-coachee-btn">
              <UserPlus className="w-4 h-4" />
              Nuevo Coachee
            </Button>
          </Link>
        </div>

        {/* Stats and Activity Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 animate-stagger">
          {stats.map((stat, index) => (
            <Card key={index} className="glass-card card-hover">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${stat.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <stat.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="label-uppercase">{stat.label}</p>
                    <p className="text-heading text-3xl text-slate-800 mt-1">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {/* Activity Card */}
          <Card className="glass-card card-hover" data-testid="activity-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="label-uppercase">Actividad Reciente</p>
                  <p className="text-heading text-3xl text-slate-800 mt-1">{activities.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Three Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coachees List */}
          <Card className="glass-card">
            <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
              <CardTitle className="text-heading text-lg text-slate-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-900" />
                Mis Coachees
              </CardTitle>
              <Link to="/coachees">
                <Button variant="ghost" size="sm" className="text-blue-900 hover:text-blue-800">
                  Ver todos
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Cargando...
                </div>
              ) : coachees.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-medium text-sm mb-2">No tienes coachees aún</p>
                  <Link to="/coachees/new">
                    <Button size="sm" className="btn-secondary">
                      <UserPlus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto scrollbar-thin">
                  {coachees.slice(0, 5).map((coachee) => (
                    <Link 
                      key={coachee.id_coachee} 
                      to={`/coachees/${coachee.id_coachee}`}
                      className="block p-3 flex items-center justify-between table-row-hover group"
                      data-testid={`coachee-item-${coachee.id_coachee}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-900 to-sky-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                          {coachee.nombre?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 group-hover:text-blue-900 transition-colors text-sm">
                            {coachee.nombre}
                          </p>
                          <p className="text-xs text-slate-500">{coachee.email || 'Sin email'}</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-900 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Section */}
          <Card className="glass-card" data-testid="activity-section">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-heading text-lg text-slate-800 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                Actividad Reciente
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingActivity ? (
                <div className="p-6 text-center text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Cargando...
                </div>
              ) : activities.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Clock className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-medium text-sm">Sin actividad reciente</p>
                  <p className="text-slate-500 text-xs mt-1">
                    La actividad aparecerá aquí
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto scrollbar-thin">
                  {activities.slice(0, 8).map((activity, index) => {
                    const ActivityIcon = getActivityIcon(activity.type);
                    const colorClass = getActivityColor(activity.type);
                    return (
                      <div
                        key={index}
                        className="p-3 flex items-center gap-3"
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <ActivityIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-800 text-sm truncate">
                            {activity.message}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatDate(activity.date)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents Section */}
          <Card className="glass-card" data-testid="documents-section">
            <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
              <CardTitle className="text-heading text-lg text-slate-800 flex items-center gap-2">
                <Folder className="w-5 h-5 text-blue-900" />
                Documentos
              </CardTitle>
              <Link to="/documents">
                <Button variant="ghost" size="sm" className="text-blue-900 hover:text-blue-800">
                  Ver todos
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="p-0">
              {loadingDocs ? (
                <div className="p-6 text-center text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Cargando...
                </div>
              ) : documents.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Folder className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-medium text-sm">Sin documentos</p>
                  <p className="text-slate-500 text-xs mt-1">
                    No hay documentos disponibles
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto scrollbar-thin">
                  {documents.slice(0, 5).map((doc) => {
                    const FileIcon = getFileIcon(doc.mimeType);
                    return (
                      <a
                        key={doc.id}
                        href={doc.webViewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 flex items-center justify-between table-row-hover group"
                        data-testid={`document-item-${doc.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            doc.isFolder 
                              ? 'bg-amber-100 text-amber-600' 
                              : 'bg-blue-100 text-blue-600'
                          }`}>
                            <FileIcon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-800 group-hover:text-blue-900 transition-colors text-sm truncate">
                              {doc.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {formatDate(doc.modifiedTime)}
                            </p>
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-900 flex-shrink-0 ml-2" />
                      </a>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
